import { Form, Input, Button, Space, message, Tag, Switch } from 'antd';

interface EmailTemplateFormProps {
  template?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const VARIABLES = [
  '{name}',
  '{email}',
  '{event}',
  '{topic}',
  '{rating}',
  '{company}',
  '{date}',
];

export default function EmailTemplateForm({
  template,
  onSuccess,
  onCancel,
}: EmailTemplateFormProps) {
  const [form] = Form.useForm();

  const insertVariable = (variable: string, field: string) => {
    const currentValue = form.getFieldValue(field) || '';
    form.setFieldValue(field, currentValue + variable);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        name: values.name,
        description: values.description,
        subject: values.subject,
        htmlContent: values.htmlContent,
        textContent: values.textContent,
        variables: VARIABLES,
        isDefault: values.isDefault || false,
        isActive: values.isActive !== undefined ? values.isActive : true,
      };

      const url = template
        ? `/api/v1/feedback/email-templates/${template.id}`
        : '/api/v1/feedback/email-templates';

      const method = template ? 'PUT' : 'POST';

      await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });

      message.success(
        template ? 'Template updated successfully' : 'Template created successfully'
      );
      onSuccess();
    } catch (error) {
      message.error('Failed to save template');
    }
  };

  const initialValues = template || { isActive: true, isDefault: false };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Form.Item
        label="Template Name"
        name="name"
        rules={[{ required: true, message: 'Please enter template name' }]}
      >
        <Input placeholder="MaximoWorld Thank You" />
      </Form.Item>

      <Form.Item label="Description" name="description">
        <Input.TextArea rows={2} placeholder="Template description..." />
      </Form.Item>

      <Form.Item
        label="Email Subject"
        name="subject"
        rules={[{ required: true, message: 'Please enter subject' }]}
      >
        <Input placeholder="Thank You for Visiting THIRAN at {event}!" />
      </Form.Item>

      <Form.Item label="Available Variables">
        <Space wrap>
          {VARIABLES.map((variable) => (
            <Tag
              key={variable}
              color="blue"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                const currentFocus = document.activeElement;
                if (currentFocus?.id === 'htmlContent') {
                  insertVariable(variable, 'htmlContent');
                } else if (currentFocus?.id === 'textContent') {
                  insertVariable(variable, 'textContent');
                } else {
                  insertVariable(variable, 'subject');
                }
              }}
            >
              {variable}
            </Tag>
          ))}
        </Space>
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
          Click a variable to insert it at cursor position
        </div>
      </Form.Item>

      <Form.Item
        label="HTML Content"
        name="htmlContent"
        rules={[{ required: true, message: 'Please enter HTML content' }]}
      >
        <Input.TextArea
          id="htmlContent"
          rows={10}
          placeholder="<p>Hi {name},</p>..."
        />
      </Form.Item>

      <Form.Item
        label="Plain Text Content"
        name="textContent"
        rules={[{ required: true, message: 'Please enter text content' }]}
      >
        <Input.TextArea
          id="textContent"
          rows={6}
          placeholder="Hi {name},..."
        />
      </Form.Item>

      <Space>
        <Form.Item label="Set as Default" name="isDefault" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Space>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            {template ? 'Update' : 'Create'}
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
